import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { FieldExplainer } from '@/components/calculator/FieldExplainer';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ChartContainer,
  ChartLegendPills,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig
} from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import {
  TAX_PRESETS,
  TAX_PRESET_LABELS,
  buildAll,
  computeBreakevenSweep,
  computeSensitivity,
  applyDiscount,
  resolveTaxRates,
  fmt,
  pctFmt,
  type CalcSettings,
  type FilingStatus,
  type LiquidityType,
  type Opportunity,
  type OpportunityResult,
  type RsuVestFrequency,
  type TaxPresetKey,
  type TaxRates,
  type TenderCadence
} from '@/engines/job-offer-engine';
import {
  getJobOfferGlobalExplainers,
  getJobOfferOfferExplainers
} from '@/content/field-explainers/job-offer';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { copyToClipboard } from './shared';

type ExplainerMap = Map<string, FieldExplainerContent>;

const MAX_OPPORTUNITIES = 3;
const DEFAULT_HORIZON = 4;
const MIN_HORIZON = 3;
const MAX_HORIZON = 7;

const OPP_COLORS = ['#60a5fa', '#34d399', '#f59e0b'];

type PageProps = { route: RouteDefinition };

type ShareableState = {
  opps: Opportunity[];
  settings: CalcSettings;
  sensitivity: SensitivityInputs;
};

type SensitivityInputs = {
  bearMult: number;
  bullMult: number;
  probBear: number;
  probBase: number;
  probBull: number;
};

type OfferPresetKey = 'public-rsu' | 'late-stage-rsu' | 'startup-options' | 'ipo-tender';

type QualitySeverity = 'attention' | 'warning' | 'good';

type OfferQualitySignal = {
  id: string;
  offerName: string;
  severity: QualitySeverity;
  title: string;
  detail: string;
};

type DecisionBrief = {
  winnerName: string;
  winnerIndex: number;
  runnerUpName: string | null;
  spread: number;
  winnerNetWorth: number;
  winnerCumCash: number;
  winnerCashShare: number;
  runnerUpCashShare: number | null;
  winnerIlliquid: number;
  downsideGap: number | null;
  summary: string;
};

const offerPresetLabels: Record<OfferPresetKey, string> = {
  'public-rsu': 'Public RSU',
  'late-stage-rsu': 'Late-stage private',
  'startup-options': 'Startup options',
  'ipo-tender': 'IPO or tender'
};

/**
 * Format a raw valuation number (whatever unit the user typed: $B, $M, $/share)
 * compactly without forcing a $ prefix, since the unit is user-defined.
 */
function fmtValuation(v: number): string {
  if (!Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  const s =
    a >= 1e9
      ? `${(a / 1e9).toFixed(2)}B`
      : a >= 1e6
        ? `${(a / 1e6).toFixed(2)}M`
        : a >= 1e3
          ? `${(a / 1e3).toFixed(1)}k`
          : a >= 10
            ? `${Math.round(a)}`
            : a > 0
              ? a.toFixed(2)
              : '0';
  return v < 0 ? `-${s}` : s;
}

function defaultValuationPath(base: number, growthPerYear: number, horizon: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < horizon; i += 1) {
    out.push(base * Math.pow(1 + growthPerYear, i + 1));
  }
  return out;
}

function makeDefaultOpportunity(idx: number, horizon: number): Opportunity {
  const id = `opp-${idx + 1}`;
  const grantDateValuation = 1;
  const valuationByYear = defaultValuationPath(grantDateValuation, 0.25, horizon);
  return {
    id,
    name: `Offer ${idx + 1}`,
    color: OPP_COLORS[idx % OPP_COLORS.length],
    baseSalary: 200_000,
    baseSalaryGrowthPct: 0.05,
    signingBonus: 0,
    targetBonusPct: 0.15,
    rsuEnabled: true,
    rsuTotalValue: 400_000,
    rsuVestYears: 4,
    rsuCliffMonths: 12,
    rsuVestFrequency: 'quarterly',
    rsuDoubleTrigger: false,
    rsuAnnualRefresh: 0,
    optionsEnabled: false,
    optionCount: 0,
    strikePrice: 1,
    currentFMV: 1,
    optionVestYears: 4,
    optionCliffMonths: 12,
    isoFraction: 1,
    refreshAnnualShares: 0,
    earlyExercise: false,
    qsbsEligible: false,
    dilutionEnabled: false,
    annualDilutionPct: 0,
    cashlessExercise: true,
    existingEquityValue: 0,
    existingPoolSellFrac: 0,
    liquidityType: 'public',
    ipoYear: 0,
    ipoLockupMonths: 6,
    tenderEnabled: false,
    tenderFirstYear: 2,
    tenderCadence: 'annual',
    tenderCapPct: 0.2,
    tenderPriceFrac: 1,
    donationMatchingEnabled: false,
    donationFrac: 0,
    donationMatchRatio: 1,
    donationMaxPct: 0.25,
    valuationByYear,
    grantDateValuation
  };
}

function makePresetOpportunity(preset: OfferPresetKey, idx: number, horizon: number): Opportunity {
  const base = makeDefaultOpportunity(idx, horizon);

  if (preset === 'public-rsu') {
    return {
      ...base,
      name: 'Public RSU offer',
      baseSalary: 230_000,
      signingBonus: 50_000,
      targetBonusPct: 0.2,
      rsuEnabled: true,
      rsuTotalValue: 600_000,
      rsuDoubleTrigger: false,
      rsuAnnualRefresh: 125_000,
      optionsEnabled: false,
      liquidityType: 'public',
      tenderEnabled: false,
      grantDateValuation: 1,
      valuationByYear: defaultValuationPath(1, 0.08, horizon)
    };
  }

  if (preset === 'late-stage-rsu') {
    return {
      ...base,
      name: 'Late-stage private RSU',
      baseSalary: 215_000,
      signingBonus: 25_000,
      targetBonusPct: 0.15,
      rsuEnabled: true,
      rsuTotalValue: 850_000,
      rsuDoubleTrigger: true,
      rsuAnnualRefresh: 100_000,
      optionsEnabled: false,
      liquidityType: 'ipo',
      ipoYear: Math.min(4, horizon),
      tenderEnabled: false,
      grantDateValuation: 12,
      valuationByYear: defaultValuationPath(12, 0.18, horizon)
    };
  }

  if (preset === 'startup-options') {
    return {
      ...base,
      name: 'Options-heavy startup',
      baseSalary: 175_000,
      signingBonus: 0,
      targetBonusPct: 0,
      rsuEnabled: false,
      optionsEnabled: true,
      optionCount: 120_000,
      strikePrice: 1.25,
      currentFMV: 1.25,
      isoFraction: 0.8,
      earlyExercise: true,
      qsbsEligible: true,
      cashlessExercise: false,
      dilutionEnabled: true,
      annualDilutionPct: 0.18,
      liquidityType: 'none',
      tenderEnabled: false,
      grantDateValuation: 1.25,
      valuationByYear: defaultValuationPath(1.25, 0.4, horizon)
    };
  }

  return {
    ...base,
    name: 'IPO or tender scenario',
    baseSalary: 205_000,
    signingBonus: 30_000,
    targetBonusPct: 0.12,
    rsuEnabled: true,
    rsuTotalValue: 500_000,
    rsuDoubleTrigger: true,
    optionsEnabled: true,
    optionCount: 40_000,
    strikePrice: 3.5,
    currentFMV: 4,
    isoFraction: 0.5,
    cashlessExercise: true,
    dilutionEnabled: true,
    annualDilutionPct: 0.1,
    liquidityType: 'ipo',
    ipoYear: Math.min(3, horizon),
    tenderEnabled: true,
    tenderFirstYear: Math.min(2, horizon),
    tenderCadence: 'annual',
    tenderCapPct: 0.2,
    tenderPriceFrac: 0.85,
    grantDateValuation: 4,
    valuationByYear: defaultValuationPath(4, 0.25, horizon)
  };
}

function makeDefaultSettings(): CalcSettings {
  return {
    timeHorizon: DEFAULT_HORIZON,
    npvEnabled: false,
    npvDiscountRate: 0.08,
    taxPreset: 'nyc-single-2026',
    filingStatus: 'single',
    customRates: undefined
  };
}

function defaultSensitivity(): SensitivityInputs {
  return { bearMult: 0.5, bullMult: 2, probBear: 0.25, probBase: 0.5, probBull: 0.25 };
}

function encodeState(state: ShareableState): string {
  try {
    const json = JSON.stringify(state);
    return typeof window === 'undefined' ? '' : window.btoa(unescape(encodeURIComponent(json)));
  } catch {
    return '';
  }
}

function decodeState(encoded: string): Partial<ShareableState> | null {
  try {
    if (typeof window === 'undefined') return null;
    const json = decodeURIComponent(escape(window.atob(encoded)));
    return JSON.parse(json) as Partial<ShareableState>;
  } catch {
    return null;
  }
}

function readInitialState(horizon: number): ShareableState {
  if (typeof window === 'undefined') {
    return {
      opps: [makeDefaultOpportunity(0, horizon), makeDefaultOpportunity(1, horizon)],
      settings: makeDefaultSettings(),
      sensitivity: defaultSensitivity()
    };
  }
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('d');
  if (raw) {
    const parsed = decodeState(raw);
    if (parsed?.opps && parsed?.settings) {
      return {
        opps: parsed.opps,
        settings: { ...makeDefaultSettings(), ...parsed.settings },
        sensitivity: { ...defaultSensitivity(), ...(parsed.sensitivity ?? {}) }
      };
    }
  }
  return {
    opps: [makeDefaultOpportunity(0, horizon), makeDefaultOpportunity(1, horizon)],
    settings: makeDefaultSettings(),
    sensitivity: defaultSensitivity()
  };
}

function resizeValuationPath(current: number[], nextHorizon: number, grantDateVal: number): number[] {
  if (current.length === nextHorizon) return current.slice();
  if (current.length > nextHorizon) return current.slice(0, nextHorizon);
  const out = current.slice();
  const last = out.length > 0 ? out[out.length - 1] : grantDateVal;
  const growth = out.length > 1 ? out[out.length - 1] / out[out.length - 2] : 1.25;
  let prev = last;
  while (out.length < nextHorizon) {
    prev = prev * growth;
    out.push(prev);
  }
  return out;
}

function getValuationMultiple(opp: Opportunity, horizon: number): number {
  const terminal = opp.valuationByYear[Math.max(0, horizon - 1)] ?? opp.grantDateValuation;
  if (!Number.isFinite(terminal) || !Number.isFinite(opp.grantDateValuation) || opp.grantDateValuation <= 0) {
    return 0;
  }
  return terminal / opp.grantDateValuation;
}

function isPrivateOrIlliquid(opp: Opportunity): boolean {
  return opp.liquidityType !== 'public' || opp.rsuDoubleTrigger || opp.tenderEnabled || opp.optionsEnabled;
}

function buildQualitySignals(opps: Opportunity[], settings: CalcSettings): OfferQualitySignal[] {
  const signals: OfferQualitySignal[] = [];

  opps.forEach((opp) => {
    const multiple = getValuationMultiple(opp, settings.timeHorizon);

    if (isPrivateOrIlliquid(opp) && settings.timeHorizon >= 6) {
      signals.push({
        id: `${opp.id}-long-horizon`,
        offerName: opp.name,
        severity: 'attention',
        title: 'Long horizon depends on staying power',
        detail: 'A six- or seven-year comparison can overvalue long-dated equity. Check the three- and four-year result before letting terminal value decide.'
      });
    }

    if (opp.optionsEnabled && !opp.dilutionEnabled) {
      signals.push({
        id: `${opp.id}-missing-dilution`,
        offerName: opp.name,
        severity: 'warning',
        title: 'Options modeled without dilution',
        detail: 'Private-company option grants are usually diluted by future financing and hiring. Add a dilution assumption before trusting the upside case.'
      });
    }

    if (opp.tenderEnabled && opp.liquidityType !== 'public' && opp.tenderCapPct > 0.25) {
      signals.push({
        id: `${opp.id}-tender-reliance`,
        offerName: opp.name,
        severity: 'warning',
        title: 'Tender liquidity may be too generous',
        detail: 'Tender windows are discretionary and often capped. Treat this as a scenario, not a guaranteed exit path.'
      });
    }

    if (multiple >= 4 && isPrivateOrIlliquid(opp)) {
      signals.push({
        id: `${opp.id}-valuation-upside`,
        offerName: opp.name,
        severity: 'attention',
        title: 'Outcome relies on a large valuation multiple',
        detail: `The terminal valuation is ${multiple.toFixed(1)}x the grant-date value. Run a flat or 1.5x case before accepting the headline ranking.`
      });
    }

    if (opp.qsbsEligible && !opp.earlyExercise) {
      signals.push({
        id: `${opp.id}-qsbs-clock`,
        offerName: opp.name,
        severity: 'warning',
        title: 'QSBS needs acquisition timing',
        detail: 'QSBS treatment usually requires acquiring shares early and holding for five years. Confirm 83(b), exercise cost, and issuer eligibility with a tax professional.'
      });
    }

    if (opp.earlyExercise && opp.optionCount * opp.strikePrice > opp.baseSalary * 0.5) {
      signals.push({
        id: `${opp.id}-exercise-cost`,
        offerName: opp.name,
        severity: 'warning',
        title: 'Early-exercise check is material',
        detail: 'The strike outlay is more than half of annual base salary. Confirm you can lose that capital without damaging your financial runway.'
      });
    }

    if (opp.rsuDoubleTrigger && (opp.ipoYear === 0 || opp.ipoYear > settings.timeHorizon)) {
      signals.push({
        id: `${opp.id}-double-trigger-lockup`,
        offerName: opp.name,
        severity: 'attention',
        title: 'Double-trigger RSUs may stay locked',
        detail: 'Vested private RSUs do not become liquid or taxable until a qualifying event. Model a no-liquidity case if the IPO year is uncertain.'
      });
    }
  });

  if (settings.taxPreset === 'wa-tx-fl-single' && opps.some(isPrivateOrIlliquid)) {
    signals.push({
      id: 'global-tax-preset',
      offerName: 'All offers',
      severity: 'attention',
      title: 'Tax preset assumes no state income tax',
      detail: 'If you may move for the role or trigger income in a different state, compare against a high-tax preset before choosing.'
    });
  }

  if (signals.length === 0) {
    signals.push({
      id: 'quality-clear',
      offerName: 'All offers',
      severity: 'good',
      title: 'No major modeling gaps detected',
      detail: 'The current inputs cover liquidity, taxes, and valuation risk well enough for a first-pass comparison. Still run at least one conservative case.'
    });
  }

  return signals;
}

function buildDecisionBrief(
  opps: Opportunity[],
  results: OpportunityResult[],
  settings: CalcSettings,
  sensitivityOutcome: ReturnType<typeof computeSensitivity> | null,
  sensitivityIdx: number
): DecisionBrief | null {
  if (opps.length === 0 || results.length === 0) return null;

  const ranked = results
    .map((result, index) => {
      const last = result.years[settings.timeHorizon - 1];
      const netWorth = last?.netWorth ?? 0;
      const cumCash = last?.cumCash ?? 0;
      const illiquid = Math.max(0, netWorth - cumCash);
      return {
        index,
        name: opps[index]?.name ?? `Offer ${index + 1}`,
        netWorth,
        cumCash,
        illiquid,
        cashShare: netWorth > 0 ? cumCash / netWorth : 0
      };
    })
    .sort((a, b) => b.netWorth - a.netWorth);

  const winner = ranked[0];
  const runnerUp = ranked[1] ?? null;
  const spread = runnerUp ? winner.netWorth - runnerUp.netWorth : 0;
  const downsideGap =
    sensitivityOutcome && sensitivityIdx === winner.index ? winner.netWorth - sensitivityOutcome.bearNW : null;

  const summaryParts = [
    `${winner.name} currently leads by ${runnerUp ? fmt(spread) : 'default'} at year ${settings.timeHorizon}.`,
    winner.cashShare < 0.55
      ? 'The lead depends heavily on equity or illiquid value, so downside and liquidity assumptions deserve extra weight.'
      : 'The leading case is supported by a comparatively strong cash component, which makes the result less dependent on exit timing.'
  ];

  if (downsideGap !== null && downsideGap > 0) {
    summaryParts.push(`The selected sensitivity bear case gives back ${fmt(downsideGap)} of that headline value.`);
  }

  return {
    winnerName: winner.name,
    winnerIndex: winner.index,
    runnerUpName: runnerUp?.name ?? null,
    spread,
    winnerNetWorth: winner.netWorth,
    winnerCumCash: winner.cumCash,
    winnerCashShare: winner.cashShare,
    runnerUpCashShare: runnerUp?.cashShare ?? null,
    winnerIlliquid: winner.illiquid,
    downsideGap,
    summary: summaryParts.join(' ')
  };
}

export function JobOfferCalculatorPage({ route }: PageProps) {
  const { toast } = useToast();
  const [initial] = useState<ShareableState>(() => readInitialState(DEFAULT_HORIZON));
  const [opps, setOpps] = useState<Opportunity[]>(initial.opps);
  const [settings, setSettings] = useState<CalcSettings>(initial.settings);
  const [sensitivity, setSensitivity] = useState<SensitivityInputs>(initial.sensitivity);
  const [breakevenIdx, setBreakevenIdx] = useState(0);
  const [sensitivityIdx, setSensitivityIdx] = useState(0);

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  useEffect(() => {
    setOpps((prev) =>
      prev.map((opp) => ({
        ...opp,
        valuationByYear: resizeValuationPath(opp.valuationByYear, settings.timeHorizon, opp.grantDateValuation)
      }))
    );
  }, [settings.timeHorizon]);

  const results = useMemo(() => buildAll(opps, settings), [opps, settings]);

  function updateOpp(idx: number, patch: Partial<Opportunity>) {
    setOpps((prev) => prev.map((opp, i) => (i === idx ? { ...opp, ...patch } : opp)));
  }

  function updateValuation(idx: number, year: number, value: number) {
    setOpps((prev) =>
      prev.map((opp, i) => {
        if (i !== idx) return opp;
        const nextPath = opp.valuationByYear.slice();
        nextPath[year] = value;
        return { ...opp, valuationByYear: nextPath };
      })
    );
  }

  function addOpportunity() {
    if (opps.length >= MAX_OPPORTUNITIES) return;
    setOpps((prev) => [...prev, makeDefaultOpportunity(prev.length, settings.timeHorizon)]);
  }

  function removeOpportunity(idx: number) {
    if (opps.length <= 1) return;
    setOpps((prev) => prev.filter((_, i) => i !== idx));
    setBreakevenIdx(0);
    setSensitivityIdx(0);
  }

  const chartConfig = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = {};
    opps.forEach((opp) => {
      cfg[opp.id] = { label: opp.name, color: opp.color };
    });
    return cfg;
  }, [opps]);

  const netWorthChartData = useMemo(() => {
    const rows: Array<Record<string, number | string>> = [];
    for (let y = 0; y < settings.timeHorizon; y += 1) {
      const row: Record<string, number | string> = { year: `Y${y + 1}` };
      results.forEach((r) => {
        row[r.id] = Math.round(r.years[y]?.netWorth ?? 0);
      });
      rows.push(row);
    }
    return rows;
  }, [results, settings.timeHorizon]);

  const cumCashChartData = useMemo(() => {
    const rows: Array<Record<string, number | string>> = [];
    for (let y = 0; y < settings.timeHorizon; y += 1) {
      const row: Record<string, number | string> = { year: `Y${y + 1}` };
      results.forEach((r) => {
        row[r.id] = Math.round(r.years[y]?.cumCash ?? 0);
      });
      rows.push(row);
    }
    return rows;
  }, [results, settings.timeHorizon]);

  const finalOutcomes = useMemo(() => {
    return results.map((r, i) => {
      const last = r.years[settings.timeHorizon - 1];
      const npv = settings.npvEnabled
        ? applyDiscount(r.years, settings.npvDiscountRate, (y) => y.netCash)
        : null;
      return {
        id: r.id,
        name: opps[i].name,
        color: opps[i].color,
        netWorth: last?.netWorth ?? 0,
        cumCash: last?.cumCash ?? 0,
        heldVal: last?.heldVal ?? 0,
        pool: (last?.poolAfter ?? 0) + (last?.existingPoolAfter ?? 0),
        preIpoPaper: last?.preIpoPaperValue ?? 0,
        npvCash: npv
      };
    });
  }, [results, opps, settings.npvEnabled, settings.npvDiscountRate, settings.timeHorizon]);

  const bestNwIdx = finalOutcomes.reduce(
    (best, cur, i) => (cur.netWorth > finalOutcomes[best].netWorth ? i : best),
    0
  );

  const sweepData = useMemo(() => {
    if (opps.length === 0 || breakevenIdx >= opps.length) return [];
    const points = computeBreakevenSweep(opps[breakevenIdx], settings);
    return points.map((p) => ({ finalVal: p.finalVal, netWorth: Math.round(p.sweepNetWorth) }));
  }, [opps, breakevenIdx, settings]);

  const sensitivityOutcome = useMemo(() => {
    if (opps.length === 0 || sensitivityIdx >= opps.length) return null;
    return computeSensitivity(opps[sensitivityIdx], settings, sensitivity);
  }, [opps, sensitivityIdx, settings, sensitivity]);

  const qualitySignals = useMemo(() => buildQualitySignals(opps, settings), [opps, settings]);

  const decisionBrief = useMemo(
    () => buildDecisionBrief(opps, results, settings, sensitivityOutcome, sensitivityIdx),
    [opps, results, settings, sensitivityOutcome, sensitivityIdx]
  );

  const taxRates = useMemo(
    () => resolveTaxRates(settings.taxPreset, settings.filingStatus, settings.customRates),
    [settings.taxPreset, settings.filingStatus, settings.customRates]
  );

  const offerExplainers = useMemo<ExplainerMap[]>(
    () =>
      results.map((result, i) => {
        const entries = getJobOfferOfferExplainers({ offer: opps[i], result, settings, taxRates });
        return new Map(entries.map((entry) => [entry.id, entry]));
      }),
    [results, opps, settings, taxRates]
  );

  const globalExplainers = useMemo(() => {
    const entries = getJobOfferGlobalExplainers({
      settings,
      taxRates,
      sensitivity,
      sensitivityOutcome,
      sensitivityOfferName: opps[sensitivityIdx]?.name ?? null,
      brief: decisionBrief,
      offers: finalOutcomes.map((o) => ({ name: o.name, netWorth: o.netWorth, cumCash: o.cumCash }))
    });
    return { entries, byId: new Map(entries.map((entry) => [entry.id, entry])) as ExplainerMap };
  }, [settings, taxRates, sensitivity, sensitivityOutcome, opps, sensitivityIdx, decisionBrief, finalOutcomes]);

  // The guide walks the shared settings once, then the leading offer's fields with
  // that offer's live numbers. Repeating an identical block for every offer would
  // add length without adding information.
  const guideEntries = useMemo(() => {
    // Track the leader by index, not name: two offers can share a name, and a
    // decoded share URL can legitimately carry zero offers.
    const leaderIdx = decisionBrief?.winnerIndex ?? bestNwIdx;
    if (!opps[leaderIdx] || !results[leaderIdx]) {
      return globalExplainers.entries;
    }
    const offerEntries = getJobOfferOfferExplainers({
      offer: opps[leaderIdx],
      result: results[leaderIdx],
      settings,
      taxRates
    });
    return [
      ...globalExplainers.entries,
      ...offerEntries.map((entry) => ({ ...entry, id: `offer-${entry.id}` }))
    ];
  }, [globalExplainers.entries, opps, results, settings, taxRates, decisionBrief, bestNwIdx]);

  const guideLeaderName = decisionBrief?.winnerName ?? opps[bestNwIdx]?.name ?? 'the leading offer';

  function applyPreset(preset: OfferPresetKey, idx: number) {
    const next = makePresetOpportunity(preset, idx, settings.timeHorizon);
    setOpps((prev) => {
      if (idx < prev.length) {
        return prev.map((opp, i) => (i === idx ? { ...next, id: opp.id, color: opp.color } : opp));
      }
      if (prev.length >= MAX_OPPORTUNITIES) {
        return prev;
      }
      return [...prev, next];
    });
    setBreakevenIdx(0);
    setSensitivityIdx(0);
  }

  async function onShareClick() {
    if (typeof window === 'undefined') return;
    const encoded = encodeState({ opps, settings, sensitivity });
    const path = route.canonicalPath === '/' || route.canonicalPath.endsWith('/') ? route.canonicalPath : `${route.canonicalPath}/`;
    const url = `${window.location.origin}${path}?d=${encoded}`;
    const ok = await copyToClipboard(url);
    toast({
      title: ok ? 'Share link copied' : 'Share link ready',
      description: ok ? url : `Copy manually: ${url}`,
      variant: ok ? 'success' : 'warning'
    });
  }

  return (
    <AppLayout wide>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Job Offer Comparison Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Compare two offers side by side. Model cash, RSUs, and options with double-trigger vesting, tender windows,
          early exercise, QSBS, and post-IPO drawdown — then pressure-test the outcome across valuation scenarios.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <GlobalSettingsPanel
        settings={settings}
        onChange={setSettings}
        onShareClick={onShareClick}
        explainers={globalExplainers.byId}
      />

      <GuidedPresetPanel opps={opps} onApplyPreset={applyPreset} />

      <section className="grid gap-5 lg:grid-cols-2">
        {opps.map((opp, idx) => (
          <OpportunityCard
            key={opp.id}
            opp={opp}
            idx={idx}
            horizon={settings.timeHorizon}
            onUpdate={(patch) => updateOpp(idx, patch)}
            onUpdateValuation={(year, value) => updateValuation(idx, year, value)}
            onRemove={opps.length > 1 ? () => removeOpportunity(idx) : undefined}
            explainers={offerExplainers[idx] ?? new Map()}
          />
        ))}
        {opps.length < MAX_OPPORTUNITIES ? (
          <Card className="brand-frame flex items-center justify-center border-dashed p-8 text-center">
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Add another offer to compare side by side. Up to {MAX_OPPORTUNITIES} offers total.
              </p>
              <Button variant="secondary" onClick={addOpportunity}>
                Add offer
              </Button>
            </div>
          </Card>
        ) : null}
      </section>

      <OfferQualityPanel signals={qualitySignals} />

      {decisionBrief ? <DecisionBriefPanel brief={decisionBrief} explainers={globalExplainers.byId} /> : null}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {finalOutcomes.map((out, i) => (
          <ResultSummaryCard
            key={out.id}
            label={`${out.name} — net worth @ Y${settings.timeHorizon}`}
            value={fmt(out.netWorth)}
            note={`Cum cash ${fmt(out.cumCash)}${out.npvCash !== null ? ` · NPV ${fmt(out.npvCash)}` : ''}`}
            trend={i === bestNwIdx ? 'up' : 'flat'}
            explainer={offerExplainers[i]?.get('netWorth')}
          />
        ))}
      </section>

      <Tabs defaultValue="projections">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="projections">Projections</TabsTrigger>
          <TabsTrigger value="detail">Annual Detail</TabsTrigger>
          <TabsTrigger value="breakeven">Breakeven</TabsTrigger>
          <TabsTrigger value="sensitivity">Sensitivity</TabsTrigger>
          <TabsTrigger value="assumptions">Assumptions</TabsTrigger>
        </TabsList>

        <TabsContent value="projections" className="space-y-5">
          <ChartCard
            title="Net worth by year"
            description="Cumulative cash, held equity, sellable pool, and pre-IPO paper stacked into one net-worth view."
            legend={<ChartLegendPills config={chartConfig} keys={opps.map((o) => o.id)} />}
          >
            <ChartContainer config={chartConfig} family="comparison">
              <LineChart data={netWorthChartData} margin={{ top: 18, left: 12, right: 12 }}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis tickLine={false} axisLine={false} width={70} tickFormatter={(v) => fmt(Number(v))} />
                <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(v) => fmt(Number(v ?? 0))} />} />
                {opps.map((opp) => (
                  <Line key={opp.id} dataKey={opp.id} type="monotone" stroke={`var(--color-${opp.id})`} strokeWidth={2.4} dot={false} />
                ))}
              </LineChart>
            </ChartContainer>
          </ChartCard>

          <ChartCard
            title="Cumulative cash by year"
            description="Post-tax cash flow accumulated year over year, including tender, pool sales, and exercise costs."
            legend={<ChartLegendPills config={chartConfig} keys={opps.map((o) => o.id)} />}
          >
            <ChartContainer config={chartConfig} family="comparison">
              <LineChart data={cumCashChartData} margin={{ top: 18, left: 12, right: 12 }}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis tickLine={false} axisLine={false} width={70} tickFormatter={(v) => fmt(Number(v))} />
                <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(v) => fmt(Number(v ?? 0))} />} />
                {opps.map((opp) => (
                  <Line key={opp.id} dataKey={opp.id} type="monotone" stroke={`var(--color-${opp.id})`} strokeWidth={2.4} dot={false} />
                ))}
              </LineChart>
            </ChartContainer>
          </ChartCard>

          <Card className="brand-frame">
            <CardHeader>
              <CardTitle>Final outcomes — Y{settings.timeHorizon}</CardTitle>
              <CardDescription>All figures in pre-NPV nominal dollars unless NPV column shown.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Offer</TableHead>
                    <TableHead numeric>Net worth</TableHead>
                    <TableHead numeric>Cum cash</TableHead>
                    <TableHead numeric>Options held</TableHead>
                    <TableHead numeric>Pool (sellable)</TableHead>
                    <TableHead numeric>Pre-IPO paper</TableHead>
                    {settings.npvEnabled ? <TableHead numeric>NPV cash</TableHead> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {finalOutcomes.map((out, i) => (
                    <TableRow key={out.id} className={i === bestNwIdx ? 'bg-primary/5' : undefined}>
                      <TableCell>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ background: out.color }} aria-hidden />
                          {out.name}
                        </span>
                      </TableCell>
                      <TableCell numeric>{fmt(out.netWorth)}</TableCell>
                      <TableCell numeric>{fmt(out.cumCash)}</TableCell>
                      <TableCell numeric>{fmt(out.heldVal)}</TableCell>
                      <TableCell numeric>{fmt(out.pool)}</TableCell>
                      <TableCell numeric>{fmt(out.preIpoPaper)}</TableCell>
                      {settings.npvEnabled ? <TableCell numeric>{fmt(out.npvCash ?? 0)}</TableCell> : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="detail" className="space-y-5">
          {results.map((r, i) => (
            <ChartCard
              key={r.id}
              title={`${opps[i].name} — annual detail`}
              description="Per-year cash, equity taxes, tender proceeds, and aggregate net cash."
              contentClassName="overflow-x-auto"
            >
              <Table compact>
                <TableHeader>
                  <TableRow>
                    <TableHead numeric>Year</TableHead>
                    <TableHead numeric>Cash net</TableHead>
                    <TableHead numeric>RSU vest (gross)</TableHead>
                    <TableHead numeric>RSU tax</TableHead>
                    <TableHead numeric>Exercise cost</TableHead>
                    <TableHead numeric>Vest tax</TableHead>
                    <TableHead numeric>Tender net</TableHead>
                    <TableHead numeric>Pool sale net</TableHead>
                    <TableHead numeric>Net cash</TableHead>
                    <TableHead numeric>Cum cash</TableHead>
                    <TableHead numeric>Pool (held)</TableHead>
                    <TableHead numeric>Options held</TableHead>
                    <TableHead numeric>Pre-IPO paper</TableHead>
                    <TableHead numeric>Net worth</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {r.years.map((y) => (
                    <TableRow key={`${r.id}-y${y.yr}`}>
                      <TableCell numeric>Y{y.yr}</TableCell>
                      <TableCell numeric>{fmt(y.cashNet)}</TableCell>
                      <TableCell numeric>{fmt(y.rsuVestedThisYrGrossUSD)}</TableCell>
                      <TableCell numeric>{fmt(y.rsuTaxUSD)}</TableCell>
                      <TableCell numeric>{fmt(y.exerciseCost)}</TableCell>
                      <TableCell numeric>{fmt(y.vestTax)}</TableCell>
                      <TableCell numeric>{fmt(y.tender.netCash)}</TableCell>
                      <TableCell numeric>{fmt(y.poolSale.net + y.existingSale.net)}</TableCell>
                      <TableCell numeric>{fmt(y.netCash)}</TableCell>
                      <TableCell numeric>{fmt(y.cumCash)}</TableCell>
                      <TableCell numeric>{fmt(y.poolAfter + y.existingPoolAfter)}</TableCell>
                      <TableCell numeric>{fmt(y.heldVal)}</TableCell>
                      <TableCell numeric>{fmt(y.preIpoPaperValue)}</TableCell>
                      <TableCell numeric>{fmt(y.netWorth)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ChartCard>
          ))}
        </TabsContent>

        <TabsContent value="breakeven" className="space-y-5">
          <AssumptionPanel
            title="Breakeven sweep"
            description="Sweep the chosen offer's final-year valuation from 0.1× to 10× and see how its Y-horizon net worth responds."
          >
            <label className="block text-sm" htmlFor="joboffer-breakeven-offer">
              <span className="mb-1 block font-semibold text-foreground">Offer to sweep</span>
              <Select
                id="joboffer-breakeven-offer"
                value={String(breakevenIdx)}
                onChange={(e) => setBreakevenIdx(Number(e.target.value))}
                options={opps.map((opp, i) => ({ value: String(i), label: opp.name }))}
              />
            </label>
          </AssumptionPanel>

          <ChartCard
            title={`${opps[breakevenIdx]?.name ?? 'Offer'} — Y${settings.timeHorizon} net worth vs. final-year valuation`}
            description={
              opps[breakevenIdx]
                ? `Sweeping the offer's final-year valuation from 0.1× to 10× of its current ${fmtValuation(
                    opps[breakevenIdx].valuationByYear[settings.timeHorizon - 1] ?? 0
                  )} setting. X-axis values match the units you entered (grant-date valuation: ${fmtValuation(
                    opps[breakevenIdx].grantDateValuation
                  )}).`
                : 'Sweep results.'
            }
          >
            <ChartContainer config={{ netWorth: { label: 'Net worth', color: 'var(--chart-1)' } }}>
              <LineChart data={sweepData} margin={{ top: 18, left: 12, right: 30, bottom: 24 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="finalVal"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(v) => fmtValuation(Number(v))}
                  label={{
                    value: `Final-year valuation (${opps[breakevenIdx]?.name ?? 'offer'})`,
                    position: 'insideBottom',
                    offset: -8,
                    style: { fill: 'hsl(var(--muted-foreground))', fontSize: 12 }
                  }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={70}
                  tickFormatter={(v) => fmt(Number(v))}
                  label={{
                    value: 'Net worth',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: 'hsl(var(--muted-foreground))', fontSize: 12, textAnchor: 'middle' }
                  }}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      indicator="line"
                      labelFormatter={(label) => `Final valuation: ${fmtValuation(Number(label))}`}
                      formatter={(v) => fmt(Number(v ?? 0))}
                    />
                  }
                />
                <Line dataKey="netWorth" type="monotone" stroke="var(--color-netWorth)" strokeWidth={2.4} dot={false} />
              </LineChart>
            </ChartContainer>
          </ChartCard>
        </TabsContent>

        <TabsContent value="sensitivity" className="space-y-5">
          <AssumptionPanel
            title="Sensitivity inputs"
            description="Apply bear/base/bull multipliers to the offer's full valuation path and weight by probability."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block text-sm" htmlFor="joboffer-sensitivity-offer">
                <span className="mb-1 block font-semibold text-foreground">Offer</span>
                <Select
                  id="joboffer-sensitivity-offer"
                  value={String(sensitivityIdx)}
                  onChange={(e) => setSensitivityIdx(Number(e.target.value))}
                  options={opps.map((opp, i) => ({ value: String(i), label: opp.name }))}
                />
              </label>
              <NumberField id="sens-bear-mult" label="Bear multiplier" value={sensitivity.bearMult} step={0.1} min={0} explainer={globalExplainers.byId.get('bearMult')} onChange={(v) => setSensitivity((s) => ({ ...s, bearMult: v }))} />
              <NumberField id="sens-bull-mult" label="Bull multiplier" value={sensitivity.bullMult} step={0.1} min={0} explainer={globalExplainers.byId.get('bullMult')} onChange={(v) => setSensitivity((s) => ({ ...s, bullMult: v }))} />
              <NumberField id="sens-prob-bear" label="P(bear)" value={sensitivity.probBear} step={0.05} min={0} max={1} explainer={globalExplainers.byId.get('probBear')} onChange={(v) => setSensitivity((s) => ({ ...s, probBear: v }))} />
              <NumberField id="sens-prob-base" label="P(base)" value={sensitivity.probBase} step={0.05} min={0} max={1} explainer={globalExplainers.byId.get('probBase')} onChange={(v) => setSensitivity((s) => ({ ...s, probBase: v }))} />
              <NumberField id="sens-prob-bull" label="P(bull)" value={sensitivity.probBull} step={0.05} min={0} max={1} explainer={globalExplainers.byId.get('probBull')} onChange={(v) => setSensitivity((s) => ({ ...s, probBull: v }))} />
            </div>
          </AssumptionPanel>

          {sensitivityOutcome ? (
            <ChartCard
              title="Scenario net worth"
              description="Y-horizon net worth in each scenario with probability-weighted expected outcome."
            >
              <ChartContainer
                config={{
                  nw: { label: 'Net worth', color: 'var(--chart-1)' }
                }}
              >
                <BarChart
                  data={[
                    { scenario: 'Bear', nw: Math.round(sensitivityOutcome.bearNW) },
                    { scenario: 'Base', nw: Math.round(sensitivityOutcome.baseNW) },
                    { scenario: 'Bull', nw: Math.round(sensitivityOutcome.bullNW) },
                    { scenario: 'Expected', nw: Math.round(sensitivityOutcome.expectedNW) }
                  ]}
                  margin={{ top: 18, left: 12, right: 12 }}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="scenario" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis tickLine={false} axisLine={false} width={70} tickFormatter={(v) => fmt(Number(v))} />
                  <ChartTooltip cursor={false} content={<ChartTooltipContent formatter={(v) => fmt(Number(v ?? 0))} />} />
                  <Bar dataKey="nw" fill="var(--color-nw)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
            </ChartCard>
          ) : null}
        </TabsContent>

        <TabsContent value="assumptions" className="space-y-4 text-sm leading-6">
          <Card className="brand-frame">
            <CardHeader>
              <CardTitle>Tax assumptions</CardTitle>
              <CardDescription>Active preset: {TAX_PRESET_LABELS[settings.taxPreset]} · Filing: {settings.filingStatus === 'single' ? 'Single' : 'Married filing jointly'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p>
                Tax rates are held as combined federal + state + city marginal rates, then scaled down for married-filing-jointly (−4pp on ordinary and cash-effective approximations). LTCG and AMT rates do not shift with filing status.
              </p>
              <TaxRatesTable preset={settings.taxPreset} filing={settings.filingStatus} custom={settings.customRates} />
              <p className="text-muted-foreground">
                Engine assumptions: ISO $100k rule uses grant-date FMV; QSBS §1202 exempts federal-only gain up to a $10M per-issuer cap and still applies state + city at the ordinary LTCG rate; AMT pref is billed at max(state+city, 28% federal) on ISO spread at exercise. Tender windows execute in order: NSO cashless → held-LTCG → ISO cashless (disqualifying disposition). Pre-IPO double-trigger RSU vests accumulate as paper until IPO, where the entire backlog revalues as W-2.
              </p>
            </CardContent>
          </Card>

          <Card className="brand-frame">
            <CardHeader>
              <CardTitle>Scope and limitations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <ul className="ml-5 list-disc space-y-1">
                <li>Single-filer presets follow 2026 top marginal brackets; bonus income is modeled at the cash-effective rate, not the supplemental withholding rate.</li>
                <li>Dilution, if enabled, reduces per-share FMV linearly each year and is applied only to options-based opportunities.</li>
                <li>Pipeline is an approximation of unvested future value; it is not included in net worth until the corresponding vest year.</li>
                <li>This tool is educational. It is not tax, legal, or financial advice.</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description={`The settings shared by every offer, then each field on ${guideLeaderName} worked through with its own numbers. Tap the info icon beside any field for the same explanation in place.`}
          entries={guideEntries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}

// ─────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────

function GuidedPresetPanel({
  opps,
  onApplyPreset
}: {
  opps: Opportunity[];
  onApplyPreset: (preset: OfferPresetKey, idx: number) => void;
}) {
  const targetIndex = Math.min(opps.length, MAX_OPPORTUNITIES - 1);

  return (
    <Card className="brand-frame border-primary/25 bg-primary/5">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Guided offer presets</CardTitle>
            <CardDescription>
              Start from realistic equity-comp patterns, then replace the numbers with your actual offer terms.
            </CardDescription>
          </div>
          <Badge variant="outline">Decision aid</Badge>
        </div>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {(Object.keys(offerPresetLabels) as OfferPresetKey[]).map((preset) => (
          <Button
            key={preset}
            type="button"
            variant="outline"
            className="h-auto justify-start whitespace-normal px-4 py-3 text-left"
            onClick={() => onApplyPreset(preset, targetIndex)}
          >
            <span>
              <span className="block font-semibold">{offerPresetLabels[preset]}</span>
              <span className="mt-1 block text-xs font-normal text-muted-foreground">
                {preset === 'public-rsu'
                  ? 'Liquid RSUs with modest annual growth.'
                  : preset === 'late-stage-rsu'
                    ? 'Double-trigger private RSUs with IPO timing.'
                    : preset === 'startup-options'
                      ? 'Options, dilution, early exercise, and QSBS.'
                      : 'Mixed RSU/options with tender and IPO assumptions.'}
              </span>
            </span>
          </Button>
        ))}
      </CardContent>
    </Card>
  );
}

function OfferQualityPanel({ signals }: { signals: OfferQualitySignal[] }) {
  const warningCount = signals.filter((signal) => signal.severity === 'warning').length;
  const attentionCount = signals.filter((signal) => signal.severity === 'attention').length;

  return (
    <Card className="brand-frame">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Offer Quality Check</CardTitle>
            <CardDescription>
              Flags assumptions that commonly make equity-heavy offers look safer or richer than they really are.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Badge variant={warningCount > 0 ? 'destructive' : 'secondary'}>{warningCount} warnings</Badge>
            <Badge variant={attentionCount > 0 ? 'outline' : 'secondary'}>{attentionCount} review</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2">
        {signals.map((signal) => (
          <div key={signal.id} className="rounded-lg border border-border/70 bg-background/80 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  signal.severity === 'warning' ? 'destructive' : signal.severity === 'good' ? 'secondary' : 'outline'
                }
              >
                {signal.severity === 'good' ? 'clear' : signal.severity}
              </Badge>
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {signal.offerName}
              </span>
            </div>
            <p className="mt-3 text-sm font-semibold text-foreground">{signal.title}</p>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">{signal.detail}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function DecisionBriefPanel({ brief, explainers }: { brief: DecisionBrief; explainers: ExplainerMap }) {
  return (
    <Card className="brand-frame border-emerald-500/30 bg-emerald-500/5">
      <CardHeader>
        <CardTitle>Decision Brief</CardTitle>
        <CardDescription>{brief.summary}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-4">
        <ResultSummaryCard
          label="Current leader"
          value={brief.winnerName}
          note={brief.runnerUpName ? `Ahead of ${brief.runnerUpName} by ${fmt(brief.spread)}` : 'Only one offer is modeled'}
          trend="up"
          explainer={explainers.get('currentLeader')}
        />
        <ResultSummaryCard
          label="Leader cash share"
          value={pctFmt(brief.winnerCashShare)}
          note="Cash share of modeled net worth at the horizon"
          trend={brief.winnerCashShare >= 0.55 ? 'up' : 'flat'}
          explainer={explainers.get('leaderCashShare')}
        />
        <ResultSummaryCard
          label="Leader illiquid value"
          value={fmt(brief.winnerIlliquid)}
          note="Equity or paper value that depends on liquidity and valuation assumptions"
          trend={brief.winnerIlliquid > 0 ? 'flat' : 'up'}
          explainer={explainers.get('leaderIlliquid')}
        />
        <ResultSummaryCard
          label="Bear-case giveback"
          value={brief.downsideGap === null ? 'Select winner' : fmt(Math.max(brief.downsideGap, 0))}
          note="Shown when sensitivity is focused on the current leader"
          trend={brief.downsideGap && brief.downsideGap > brief.spread ? 'down' : 'flat'}
          explainer={explainers.get('bearGiveback')}
        />
      </CardContent>
    </Card>
  );
}

function GlobalSettingsPanel({
  settings,
  onChange,
  onShareClick,
  explainers
}: {
  settings: CalcSettings;
  onChange: (next: CalcSettings) => void;
  onShareClick: () => void;
  explainers: ExplainerMap;
}) {
  return (
    <AssumptionPanel
      title="Comparison settings"
      description="Horizon, filing status, and tax preset apply to every offer. Enable NPV to discount future cash flows."
    >
      <div className="grid gap-4 md:grid-cols-3">
        <label className="block text-sm">
          <span className="mb-1 flex items-center gap-2 font-semibold text-foreground">
            Time horizon (years)
            {explainers.get('timeHorizon') ? <FieldExplainer explainer={explainers.get('timeHorizon')!} /> : null}
          </span>
          <Slider
            min={MIN_HORIZON}
            max={MAX_HORIZON}
            value={settings.timeHorizon}
            onValueChange={(v) => onChange({ ...settings, timeHorizon: v })}
          />
        </label>
        <label className="block text-sm" htmlFor="joboffer-tax-preset">
          <span className="mb-1 flex items-center gap-2 font-semibold text-foreground">
            Tax preset
            {explainers.get('taxPreset') ? <FieldExplainer explainer={explainers.get('taxPreset')!} /> : null}
          </span>
          <Select
            id="joboffer-tax-preset"
            value={settings.taxPreset}
            onChange={(e) => onChange({ ...settings, taxPreset: e.target.value as TaxPresetKey })}
            options={(Object.keys(TAX_PRESET_LABELS) as TaxPresetKey[]).map((key) => ({
              value: key,
              label: TAX_PRESET_LABELS[key]
            }))}
          />
        </label>
        <label className="block text-sm" htmlFor="joboffer-filing-status">
          <span className="mb-1 flex items-center gap-2 font-semibold text-foreground">
            Filing status
            {explainers.get('filingStatus') ? <FieldExplainer explainer={explainers.get('filingStatus')!} /> : null}
          </span>
          <Select
            id="joboffer-filing-status"
            value={settings.filingStatus}
            onChange={(e) => onChange({ ...settings, filingStatus: e.target.value as FilingStatus })}
            options={[
              { value: 'single', label: 'Single' },
              { value: 'married_joint', label: 'Married filing jointly' }
            ]}
          />
        </label>
      </div>

      {settings.taxPreset === 'custom' ? (
        <div className="grid gap-4 md:grid-cols-3">
          <NumberField
            id="custom-ord"
            label="Ordinary (0-1)"
            explainer={explainers.get('custom-ord')}
            step={0.01}
            min={0}
            max={1}
            value={settings.customRates?.ord ?? TAX_PRESETS['nyc-single-2026'].ord}
            onChange={(v) => onChange({ ...settings, customRates: mergeCustom(settings.customRates, { ord: v }) })}
          />
          <NumberField
            id="custom-ltcg"
            label="LTCG (0-1)"
            explainer={explainers.get('custom-ltcg')}
            step={0.01}
            min={0}
            max={1}
            value={settings.customRates?.ltcg ?? TAX_PRESETS['nyc-single-2026'].ltcg}
            onChange={(v) => onChange({ ...settings, customRates: mergeCustom(settings.customRates, { ltcg: v }) })}
          />
          <NumberField
            id="custom-iso-sc"
            label="ISO state+city (0-1)"
            explainer={explainers.get('custom-isoStateCity')}
            step={0.01}
            min={0}
            max={1}
            value={settings.customRates?.isoStateCity ?? TAX_PRESETS['nyc-single-2026'].isoStateCity}
            onChange={(v) => onChange({ ...settings, customRates: mergeCustom(settings.customRates, { isoStateCity: v }) })}
          />
          <NumberField
            id="custom-iso-amt"
            label="ISO federal AMT (0-1)"
            explainer={explainers.get('custom-isoAmt')}
            step={0.01}
            min={0}
            max={1}
            value={settings.customRates?.isoAmt ?? TAX_PRESETS['nyc-single-2026'].isoAmt}
            onChange={(v) => onChange({ ...settings, customRates: mergeCustom(settings.customRates, { isoAmt: v }) })}
          />
          <NumberField
            id="custom-cash"
            label="Cash effective (0-1)"
            explainer={explainers.get('custom-cashEff')}
            step={0.01}
            min={0}
            max={1}
            value={settings.customRates?.cashEff ?? TAX_PRESETS['nyc-single-2026'].cashEff}
            onChange={(v) => onChange({ ...settings, customRates: mergeCustom(settings.customRates, { cashEff: v }) })}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-4 border-t border-border/60 pt-4">
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
          <Switch
            id="npv-toggle"
            checked={settings.npvEnabled}
            onCheckedChange={(checked) => onChange({ ...settings, npvEnabled: checked })}
            ariaLabel="Enable NPV"
          />
          Discount cash flows (NPV)
        </label>
        {settings.npvEnabled ? (
          <div className="w-48">
            <NumberField
              id="npv-rate"
              label="Discount rate"
              explainer={explainers.get('npvDiscountRate')}
              step={0.005}
              min={0}
              max={0.5}
              value={settings.npvDiscountRate}
              onChange={(v) => onChange({ ...settings, npvDiscountRate: v })}
            />
          </div>
        ) : null}
        <Button variant="secondary" onClick={onShareClick} className="ml-auto">
          Copy share link
        </Button>
      </div>
    </AssumptionPanel>
  );
}

function mergeCustom(current: TaxRates | undefined, patch: Partial<TaxRates>): TaxRates {
  const base = current ?? TAX_PRESETS['nyc-single-2026'];
  return { ...base, ...patch };
}

function TaxRatesTable({ preset, filing, custom }: { preset: TaxPresetKey; filing: FilingStatus; custom?: TaxRates }) {
  const base = preset === 'custom' ? custom ?? TAX_PRESETS['nyc-single-2026'] : TAX_PRESETS[preset];
  const applied =
    filing === 'married_joint'
      ? { ...base, ord: Math.max(0, base.ord - 0.04), cashEff: Math.max(0, base.cashEff - 0.04) }
      : base;
  return (
    <Table compact>
      <TableHeader>
        <TableRow>
          <TableHead>Rate</TableHead>
          <TableHead numeric>Value</TableHead>
          <TableHead>Notes</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>
          <TableCell>Ordinary</TableCell>
          <TableCell numeric>{pctFmt(applied.ord)}</TableCell>
          <TableCell>RSU vest, NSO spread, disqualifying ISO dispositions.</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>LTCG</TableCell>
          <TableCell numeric>{pctFmt(applied.ltcg)}</TableCell>
          <TableCell>Federal + NIIT + state + city for held shares over 1 year.</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>ISO state + city</TableCell>
          <TableCell numeric>{pctFmt(applied.isoStateCity)}</TableCell>
          <TableCell>Applied on ISO spread at exercise under the regular-tax path.</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>ISO AMT federal</TableCell>
          <TableCell numeric>{pctFmt(applied.isoAmt)}</TableCell>
          <TableCell>Alternative minimum tax rate on ISO spread if shares are held for LTCG.</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Cash effective</TableCell>
          <TableCell numeric>{pctFmt(applied.cashEff)}</TableCell>
          <TableCell>Blended rate on salary and bonus (below top marginal).</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

function OpportunityCard({
  opp,
  idx,
  horizon,
  onUpdate,
  onUpdateValuation,
  onRemove,
  explainers
}: {
  opp: Opportunity;
  idx: number;
  horizon: number;
  onUpdate: (patch: Partial<Opportunity>) => void;
  onUpdateValuation: (year: number, value: number) => void;
  onRemove?: () => void;
  explainers: ExplainerMap;
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <Card className="brand-frame" style={{ borderColor: `${opp.color}40` }}>
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-1">
            <label className="block">
              <span className="sr-only">Offer name</span>
              <input
                type="text"
                value={opp.name}
                onChange={(e) => onUpdate({ name: e.target.value })}
                className="w-full bg-transparent font-display text-xl text-foreground focus-visible:outline-none"
                aria-label="Offer name"
              />
            </label>
            <CardDescription>Offer {idx + 1}</CardDescription>
          </div>
          {onRemove ? (
            <Button variant="outline" size="sm" onClick={onRemove}>
              Remove
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 md:grid-cols-2">
          <NumberField id={`${opp.id}-base`} label="Base salary" explainer={explainers.get('baseSalary')} value={opp.baseSalary} min={0} step={5000} prefix="$" onChange={(v) => onUpdate({ baseSalary: v })} />
          <NumberField id={`${opp.id}-raise`} label="Raise / yr" explainer={explainers.get('baseSalaryGrowthPct')} value={opp.baseSalaryGrowthPct} step={0.005} min={0} max={0.3} suffix="%" onChange={(v) => onUpdate({ baseSalaryGrowthPct: v })} helperText="Enter as decimal (0.05 = 5%)" />
          <NumberField id={`${opp.id}-signing`} label="Signing bonus" explainer={explainers.get('signingBonus')} value={opp.signingBonus} min={0} step={5000} prefix="$" onChange={(v) => onUpdate({ signingBonus: v })} />
          <NumberField id={`${opp.id}-bonus`} label="Target bonus" explainer={explainers.get('targetBonusPct')} value={opp.targetBonusPct} step={0.01} min={0} max={1} helperText="Fraction of base" onChange={(v) => onUpdate({ targetBonusPct: v })} />
        </div>

        <div className="space-y-3 rounded-xl border border-border/60 bg-secondary/15 p-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">RSU grant</p>
            <Switch id={`${opp.id}-rsu-en`} checked={opp.rsuEnabled} onCheckedChange={(c) => onUpdate({ rsuEnabled: c })} ariaLabel="Enable RSU grant" />
          </div>
          {opp.rsuEnabled ? (
            <div className="grid gap-3 md:grid-cols-2">
              <NumberField id={`${opp.id}-rsu-total`} label="Total grant value" explainer={explainers.get('rsuTotalValue')} value={opp.rsuTotalValue} min={0} step={10_000} prefix="$" onChange={(v) => onUpdate({ rsuTotalValue: v })} />
              <NumberField id={`${opp.id}-rsu-years`} label="Vest years" explainer={explainers.get('rsuVestYears')} value={opp.rsuVestYears} min={1} max={6} parseMode="integer" onChange={(v) => onUpdate({ rsuVestYears: v })} />
              <NumberField id={`${opp.id}-rsu-cliff`} label="Cliff (months)" explainer={explainers.get('rsuCliffMonths')} value={opp.rsuCliffMonths} min={0} max={24} parseMode="integer" onChange={(v) => onUpdate({ rsuCliffMonths: v })} />
              <label className="block text-sm" htmlFor={`${opp.id}-rsu-freq`}>
                <span className="mb-1 flex items-center gap-2 font-semibold text-foreground">
                  Vest frequency
                  {explainers.get('rsuVestFrequency') ? <FieldExplainer explainer={explainers.get('rsuVestFrequency')!} /> : null}
                </span>
                <Select id={`${opp.id}-rsu-freq`} value={opp.rsuVestFrequency} onChange={(e) => onUpdate({ rsuVestFrequency: e.target.value as RsuVestFrequency })} options={[{ value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Quarterly' }, { value: 'annual', label: 'Annual' }]} />
              </label>
              <NumberField id={`${opp.id}-rsu-refresh`} label="Annual refresh value" explainer={explainers.get('rsuAnnualRefresh')} value={opp.rsuAnnualRefresh} min={0} step={5_000} prefix="$" onChange={(v) => onUpdate({ rsuAnnualRefresh: v })} />
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch id={`${opp.id}-rsu-dbl`} checked={opp.rsuDoubleTrigger} onCheckedChange={(c) => onUpdate({ rsuDoubleTrigger: c })} ariaLabel="Double-trigger RSU" />
                Double-trigger (needs liquidity event)
              </label>
            </div>
          ) : null}
        </div>

        <div className="space-y-3 rounded-xl border border-border/60 bg-secondary/15 p-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Stock options</p>
            <Switch id={`${opp.id}-opt-en`} checked={opp.optionsEnabled} onCheckedChange={(c) => onUpdate({ optionsEnabled: c })} ariaLabel="Enable options" />
          </div>
          {opp.optionsEnabled ? (
            <div className="grid gap-3 md:grid-cols-2">
              <NumberField id={`${opp.id}-opt-count`} label="Option count" explainer={explainers.get('optionCount')} value={opp.optionCount} min={0} step={1000} parseMode="integer" onChange={(v) => onUpdate({ optionCount: v })} />
              <NumberField id={`${opp.id}-opt-strike`} label="Strike price" explainer={explainers.get('strikePrice')} value={opp.strikePrice} min={0} step={0.5} prefix="$" onChange={(v) => onUpdate({ strikePrice: v })} />
              <NumberField id={`${opp.id}-opt-fmv`} label="Current FMV" explainer={explainers.get('currentFMV')} value={opp.currentFMV} min={0} step={0.5} prefix="$" onChange={(v) => onUpdate({ currentFMV: v })} />
              <NumberField id={`${opp.id}-opt-vest`} label="Vest years" explainer={explainers.get('optionVestYears')} value={opp.optionVestYears} min={1} max={6} parseMode="integer" onChange={(v) => onUpdate({ optionVestYears: v })} />
              <NumberField id={`${opp.id}-opt-iso`} label="ISO fraction" explainer={explainers.get('isoFraction')} value={opp.isoFraction} step={0.1} min={0} max={1} helperText="Remainder treated as NSO" onChange={(v) => onUpdate({ isoFraction: v })} />
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch id={`${opp.id}-cashless`} checked={opp.cashlessExercise} onCheckedChange={(c) => onUpdate({ cashlessExercise: c })} ariaLabel="Cashless exercise" />
                Cashless exercise (hold vs. pay strike)
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch id={`${opp.id}-early`} checked={opp.earlyExercise} onCheckedChange={(c) => onUpdate({ earlyExercise: c })} ariaLabel="Early exercise" />
                Early exercise + 83(b)
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch id={`${opp.id}-qsbs`} checked={opp.qsbsEligible} onCheckedChange={(c) => onUpdate({ qsbsEligible: c })} ariaLabel="QSBS eligible" />
                QSBS §1202 eligible
              </label>
            </div>
          ) : null}
        </div>

        <div className="space-y-3 rounded-xl border border-border/60 bg-secondary/15 p-3">
          <p className="text-sm font-semibold text-foreground">Liquidity & valuation</p>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-sm" htmlFor={`${opp.id}-liquidity-type`}>
              <span className="mb-1 flex items-center gap-2 font-semibold text-foreground">
                Liquidity type
                {explainers.get('liquidityType') ? <FieldExplainer explainer={explainers.get('liquidityType')!} /> : null}
              </span>
              <Select
                id={`${opp.id}-liquidity-type`}
                value={opp.liquidityType}
                onChange={(e) => onUpdate({ liquidityType: e.target.value as LiquidityType })}
                options={[
                  { value: 'public', label: 'Public company (immediate sell)' },
                  { value: 'ipo', label: 'Planned IPO in horizon' },
                  { value: 'tender-only', label: 'Tender only (no IPO)' },
                  { value: 'none', label: 'No liquidity' }
                ]}
              />
            </label>
            {opp.liquidityType === 'ipo' ? (
              <NumberField id={`${opp.id}-ipo-yr`} label="IPO year" explainer={explainers.get('ipoYear')} value={opp.ipoYear} min={1} max={horizon} parseMode="integer" onChange={(v) => onUpdate({ ipoYear: v })} />
            ) : null}
            <NumberField id={`${opp.id}-pool-sell`} label="Post-liquidity sell frac" explainer={explainers.get('existingPoolSellFrac')} value={opp.existingPoolSellFrac} step={0.05} min={0} max={1} helperText="Fraction of pool sold per year after liquidity" onChange={(v) => onUpdate({ existingPoolSellFrac: v })} />
            <NumberField id={`${opp.id}-grant-val`} label="Grant-date valuation" explainer={explainers.get('grantDateValuation')} value={opp.grantDateValuation} min={0.0001} step={0.1} onChange={(v) => onUpdate({ grantDateValuation: v })} helperText="Reference unit (e.g., $B company or $/share)" />
          </div>

          <div className="space-y-2">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              Year-by-year valuation path
              {explainers.get('valuationPath') ? <FieldExplainer explainer={explainers.get('valuationPath')!} /> : null}
            </p>
            <div className="grid gap-2 md:grid-cols-2">
              {opp.valuationByYear.slice(0, horizon).map((v, y) => (
                <NumberField
                  key={`${opp.id}-val-${y}`}
                  id={`${opp.id}-val-${y}`}
                  label={`End of Y${y + 1}`}
                  value={v}
                  step={0.1}
                  min={0}
                  onChange={(nv) => onUpdateValuation(y, nv)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3 rounded-xl border border-border/60 bg-secondary/15 p-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Tender windows</p>
            <Switch id={`${opp.id}-tender-en`} checked={opp.tenderEnabled} onCheckedChange={(c) => onUpdate({ tenderEnabled: c })} ariaLabel="Enable tender" />
          </div>
          {opp.tenderEnabled ? (
            <div className="grid gap-3 md:grid-cols-2">
              <NumberField id={`${opp.id}-tender-first`} label="First tender year" explainer={explainers.get('tenderFirstYear')} value={opp.tenderFirstYear} min={1} max={horizon} parseMode="integer" onChange={(v) => onUpdate({ tenderFirstYear: v })} />
              <label className="block text-sm" htmlFor={`${opp.id}-tender-cadence`}>
                <span className="mb-1 block font-semibold text-foreground">Cadence</span>
                <Select id={`${opp.id}-tender-cadence`} value={opp.tenderCadence} onChange={(e) => onUpdate({ tenderCadence: e.target.value as TenderCadence })} options={[{ value: 'annual', label: 'Annual' }, { value: 'semiannual', label: 'Semi-annual' }, { value: 'none', label: 'None' }]} />
              </label>
              <NumberField id={`${opp.id}-tender-cap`} label="Cap (% of vested)" explainer={explainers.get('tenderCapPct')} value={opp.tenderCapPct} step={0.05} min={0} max={1} onChange={(v) => onUpdate({ tenderCapPct: v })} />
              <NumberField id={`${opp.id}-tender-price`} label="Tender price vs FMV" explainer={explainers.get('tenderPriceFrac')} value={opp.tenderPriceFrac} step={0.05} min={0} max={2} onChange={(v) => onUpdate({ tenderPriceFrac: v })} helperText="1.0 = at FMV" />
            </div>
          ) : null}
        </div>

        <button
          type="button"
          className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
          onClick={() => setShowAdvanced((s) => !s)}
        >
          {showAdvanced ? '– Hide advanced' : '+ Show advanced'}
        </button>

        {showAdvanced ? (
          <div className="space-y-3 rounded-xl border border-border/60 bg-secondary/10 p-3">
            <div className="grid gap-3 md:grid-cols-2">
              <NumberField id={`${opp.id}-existing`} label="Existing vested equity" explainer={explainers.get('existingEquityValue')} value={opp.existingEquityValue} min={0} step={1000} prefix="$" onChange={(v) => onUpdate({ existingEquityValue: v })} />
              <NumberField id={`${opp.id}-retention-amt`} label="Retention grant" explainer={explainers.get('retentionGrant')} value={opp.retentionGrant?.amount ?? 0} min={0} step={10_000} prefix="$" onChange={(v) => onUpdate({ retentionGrant: v > 0 ? { amount: v, startYr: opp.retentionGrant?.startYr ?? 1, vestYears: opp.retentionGrant?.vestYears ?? 2 } : undefined })} />
              {opp.retentionGrant ? (
                <>
                  <NumberField id={`${opp.id}-retention-start`} label="Retention start year" value={opp.retentionGrant.startYr} min={1} max={horizon} parseMode="integer" onChange={(v) => onUpdate({ retentionGrant: { ...opp.retentionGrant!, startYr: v } })} />
                  <NumberField id={`${opp.id}-retention-years`} label="Retention vest years" value={opp.retentionGrant.vestYears} min={1} max={6} parseMode="integer" onChange={(v) => onUpdate({ retentionGrant: { ...opp.retentionGrant!, vestYears: v } })} />
                </>
              ) : null}
            </div>

            {opp.optionsEnabled ? (
              <div className="grid gap-3 md:grid-cols-2">
                <NumberField id={`${opp.id}-refresh-shares`} label="Annual refresh shares" explainer={explainers.get('refreshAnnualShares')} value={opp.refreshAnnualShares} min={0} step={1000} parseMode="integer" onChange={(v) => onUpdate({ refreshAnnualShares: v })} />
                <label className="inline-flex items-center gap-2 text-sm">
                  <Switch id={`${opp.id}-dil-en`} checked={opp.dilutionEnabled} onCheckedChange={(c) => onUpdate({ dilutionEnabled: c })} ariaLabel="Dilution" />
                  Apply annual dilution
                </label>
                {opp.dilutionEnabled ? (
                  <NumberField id={`${opp.id}-dil-pct`} label="Annual dilution %" explainer={explainers.get('annualDilutionPct')} value={opp.annualDilutionPct} step={0.01} min={0} max={0.3} onChange={(v) => onUpdate({ annualDilutionPct: v })} />
                ) : null}
              </div>
            ) : null}

            <div className="space-y-3">
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch id={`${opp.id}-donate-en`} checked={opp.donationMatchingEnabled} onCheckedChange={(c) => onUpdate({ donationMatchingEnabled: c })} ariaLabel="Donation matching" />
                Donation matching program
              </label>
              {opp.donationMatchingEnabled ? (
                <div className="grid gap-3 md:grid-cols-3">
                  <NumberField id={`${opp.id}-donate-frac`} label="Donate fraction" explainer={explainers.get('donationFrac')} value={opp.donationFrac} step={0.01} min={0} max={opp.donationMaxPct} onChange={(v) => onUpdate({ donationFrac: v })} />
                  <NumberField id={`${opp.id}-donate-ratio`} label="Match ratio" explainer={explainers.get('donationMatchRatio')} value={opp.donationMatchRatio} step={0.5} min={0} max={5} onChange={(v) => onUpdate({ donationMatchRatio: v })} helperText="1.0 = 1:1 match" />
                  <NumberField id={`${opp.id}-donate-max`} label="Program cap" explainer={explainers.get('donationMaxPct')} value={opp.donationMaxPct} step={0.05} min={0} max={1} onChange={(v) => onUpdate({ donationMaxPct: v })} />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
